<template>
    <Modal
        v-model="visibleLocal"
        :title="isEdit ? '编辑运费模板' : '新建运费模板'"
        width="1120"
        :mask-closable="false"
        :footer-hide="true"
        @on-visible-change="onVisibleChange"
    >
        <div class="edit-body">
            <div class="section-title">模板基础信息</div>
            <Form :model="form" :label-width="90" label-position="left" class="base-form">
                <Row :gutter="24">
                    <Col span="12">
                        <FormItem label="模板名称" required>
                            <Input
                                v-model="form.name"
                                placeholder="请输入模板名称"
                                :maxlength="60"
                                show-word-limit
                            />
                        </FormItem>
                    </Col>
                    <Col span="12">
                        <FormItem label="发货地" required>
                            <div class="from-address-box">
                                <Select
                                    v-model="form.from_province_id"
                                    placeholder="请选择省份"
                                    class="region-select"
                                    @on-change="onFromProvinceChange"
                                >
                                    <Option
                                        v-for="p in provinceOptions"
                                        :value="p.id"
                                        :key="p.id"
                                        >{{ p.name }}</Option
                                    >
                                </Select>
                                <Select
                                    v-model="form.from_city_id"
                                    placeholder="请选择城市"
                                    class="region-select"
                                    :disabled="!form.from_province_id"
                                    @on-change="onFromCityChange"
                                >
                                    <Option
                                        v-for="c in cityOptions"
                                        :value="c.id"
                                        :key="c.id"
                                        >{{ c.name }}</Option
                                    >
                                </Select>
                            </div>
                        </FormItem>
                    </Col>
                </Row>
                <FormItem label="是否包邮" required>
                    <div class="switch-box">
                        <i-switch
                            v-model="form.is_all_free"
                            :true-value="1"
                            :false-value="0"
                            active-color="#FF5967"
                        />
                        <span class="switch-label">全场包邮</span>
                        <span class="switch-tip">（开启后全国默认包邮，可为指定地区单独设置运费覆盖）</span>
                    </div>
                </FormItem>
            </Form>

            <div class="section-title">
                运费规则
                <span class="section-sub">（全国地区默认）</span>
            </div>
            <div :class="['default-rule', { disabled: form.is_all_free }]">
                <span class="rule-label">首件运费</span>
                <InputNumber
                    :min="1"
                    :precision="0"
                    v-model="form.first_qty"
                    :disabled="!!form.is_all_free"
                />
                <span class="rule-unit">件内</span>
                <InputNumber
                    :min="0"
                    :precision="2"
                    v-model="form.first_price"
                    :disabled="!!form.is_all_free"
                />
                <span class="rule-unit">元，每增加</span>
                <InputNumber
                    :min="1"
                    :precision="0"
                    v-model="form.more_qty"
                    :disabled="!!form.is_all_free"
                />
                <span class="rule-unit">件，增加运费</span>
                <InputNumber
                    :min="0"
                    :precision="2"
                    v-model="form.more_price"
                    :disabled="!!form.is_all_free"
                />
                <span class="rule-unit">元</span>
            </div>

            <div class="section-title">
                为指定地区设置运费
                <span class="section-sub optional">（可选）</span>
                <span class="section-tip">可为指定省市地区设置运费规则；指定地区规则优先于默认规则生效</span>
            </div>
            <div class="region-box">
                <table class="region-table">
                    <thead>
                        <tr>
                            <th class="th-province">收货地</th>
                            <th class="th-rule">运费规则</th>
                            <th class="th-action">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(rule, index) in form.setting_json" :key="index">
                            <td>
                                <Select
                                    v-model="rule.province_ids"
                                    multiple
                                    filterable
                                    placeholder="请选择指定区域，如新疆、西藏等"
                                    @on-change="() => onProvinceChange(rule)"
                                >
                                    <Option
                                        v-for="p in provinceOptions"
                                        :value="p.id"
                                        :key="p.id"
                                        >{{ p.name }}</Option
                                    >
                                </Select>
                            </td>
                            <td>
                                <div class="inline-rule">
                                    <span>首件</span>
                                    <InputNumber
                                        :min="1"
                                        :precision="0"
                                        v-model="rule.first_qty"
                                    />
                                    <span>件内</span>
                                    <InputNumber
                                        :min="0"
                                        :precision="2"
                                        v-model="rule.first_price"
                                    />
                                    <span>元，每加</span>
                                    <InputNumber
                                        :min="1"
                                        :precision="0"
                                        v-model="rule.more_qty"
                                    />
                                    <span>件加</span>
                                    <InputNumber
                                        :min="0"
                                        :precision="2"
                                        v-model="rule.more_price"
                                    />
                                    <span>元</span>
                                </div>
                            </td>
                            <td>
                                <span
                                    class="delete-link"
                                    @click="removeRegion(index)"
                                    >删除</span
                                >
                            </td>
                        </tr>
                    </tbody>
                </table>
                <Button
                    type="dashed"
                    icon="md-add"
                    class="add-region-btn"
                    @click="addRegion"
                >添加指定地区</Button>
            </div>

            <div class="section-title">备注说明</div>
            <Input
                v-model="form.memo"
                type="textarea"
                :rows="3"
                placeholder="请输入备注说明"
                :maxlength="100"
                show-word-limit
            />
            <Checkbox
                v-model="form.is_default"
                :true-value="1"
                :false-value="0"
                class="default-checkbox"
            >设为默认运费模板</Checkbox>
        </div>

        <div class="edit-footer">
            <Button @click="onCancel">取消</Button>
            <Button class="save-btn" :loading="submitting" @click="onSubmit"
                >保存模板</Button
            >
        </div>
    </Modal>
</template>

<script>
import { ShopFreightTemplateCreate, ShopFreightTemplateUpdate } from "@/api";
import region from "@/utils/region";

export default {
    name: "FreightEdit",
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        editData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            submitting: false,
            provinceOptions: [],
            cityOptions: [],
            form: this.getEmptyForm()
        };
    },
    computed: {
        visibleLocal: {
            get() {
                return this.visible;
            },
            set(val) {
                this.$emit("update:visible", val);
            }
        },
        isEdit() {
            return !!(this.editData && this.editData.id);
        }
    },
    watch: {},
    mounted() {
        this.loadProvinceOptions();
    },
    methods: {
        getEmptyForm() {
            return {
                id: null,
                name: "",
                from_province: "",
                from_province_id: null,
                from_city: "",
                from_city_id: null,
                from_zone: "",
                is_all_free: 0,
                first_qty: 1,
                first_price: 0,
                more_qty: 1,
                more_price: 0,
                memo: "",
                is_default: 0,
                setting_json: []
            };
        },
        toNumber(value, defaultValue = 0) {
            if (value === null || value === undefined || value === "") {
                return defaultValue;
            }
            const num = Number(value);
            return isNaN(num) ? defaultValue : num;
        },
        initForm() {
            if (this.editData) {
                const form = {
                    ...this.getEmptyForm(),
                    ...this.editData,
                    is_all_free: this.toNumber(this.editData.is_all_free, 0),
                    is_default: this.toNumber(this.editData.is_default, 0),
                    first_qty: this.toNumber(this.editData.first_qty, 1),
                    first_price: this.toNumber(this.editData.first_price, 0),
                    more_qty: this.toNumber(this.editData.more_qty, 1),
                    more_price: this.toNumber(this.editData.more_price, 0),
                    setting_json: (this.editData.setting_json || []).map(
                        (rule) => ({
                            ...rule,
                            province_ids: (rule.province_list || []).map(
                                (p) => p.id
                            ),
                            first_qty: this.toNumber(rule.first_qty, 1),
                            first_price: this.toNumber(rule.first_price, 0),
                            more_qty: this.toNumber(rule.more_qty, 1),
                            more_price: this.toNumber(rule.more_price, 0)
                        })
                    )
                };
                const province = this.provinceOptions.find(
                    (item) => item.name === this.editData.from_province
                );
                form.from_province_id = province ? province.id : null;

                // 同步加载城市列表并回填城市，保证城市下拉的选项与值在同一次渲染中就绪
                this.loadCities(form.from_province_id);
                const city = this.cityOptions.find(
                    (item) => item.name === this.editData.from_city
                );
                form.from_city_id = city ? city.id : null;

                this.form = form;
            } else {
                this.cityOptions = [];
                this.form = this.getEmptyForm();
            }
        },
        loadProvinceOptions() {
            this.provinceOptions = (region || []).map((p) => ({
                id: p.value,
                name: p.label
            }));
        },
        loadCities(parentId) {
            const province = (region || []).find(
                (p) => p.value === parentId || p.label === parentId
            );
            this.cityOptions =
                province && Array.isArray(province.children)
                    ? province.children.map((c) => ({
                          id: c.value,
                          name: c.label
                      }))
                    : [];
        },
        onFromProvinceChange(provinceId) {
            const province = this.provinceOptions.find(
                (item) => item.id == provinceId
            );
            this.form.from_province = province ? province.name : "";
            this.form.from_city_id = null;
            this.form.from_city = "";
            this.cityOptions = [];
            if (provinceId) {
                this.loadCities(provinceId);
            }
        },
        onFromCityChange(cityId) {
            const city = this.cityOptions.find((item) => item.id == cityId);
            this.form.from_city = city ? city.name : "";
        },
        onVisibleChange(show) {
            if (show) {
                this.initForm();
            }
        },
        addRegion() {
            this.form.setting_json.push({
                province_list: [],
                province_ids: [],
                first_qty: 1,
                first_price: 0,
                more_qty: 1,
                more_price: 0
            });
        },
        removeRegion(index) {
            this.form.setting_json.splice(index, 1);
        },
        onProvinceChange(rule) {
            rule.province_list = (rule.province_ids || []).map((id) => {
                const p = this.provinceOptions.find((item) => item.id == id);
                return { id, name: p ? p.name : "" };
            });
        },
        validate() {
            for (let i = 0; i < this.form.setting_json.length; i++) {
                const rule = this.form.setting_json[i];
                if (!rule.province_list || !rule.province_list.length) {
                    this.$layer.msg(`第${i + 1}条指定地区规则未选择收货地`);
                    return false;
                }
            }
            return true;
        },
        buildSubmitData() {
            const setting_json = this.form.setting_json.map((rule) => ({
                province_list: rule.province_list || [],
                first_qty: rule.first_qty,
                first_price: rule.first_price,
                more_qty: rule.more_qty,
                more_price: rule.more_price
            }));

            return {
                id: this.form.id,
                name: this.form.name,
                from_province: this.form.from_province,
                from_city: this.form.from_city,
                from_zone: this.form.from_zone,
                is_all_free: this.form.is_all_free,
                first_qty: this.form.first_qty,
                first_price: this.form.first_price,
                more_qty: this.form.more_qty,
                more_price: this.form.more_price,
                memo: this.form.memo,
                is_default: this.form.is_default,
                setting_json
            };
        },
        async onSubmit() {
            if (!this.validate()) return;
            const data = this.buildSubmitData();
            this.submitting = true;
            try {
                const api = this.isEdit
                    ? ShopFreightTemplateUpdate
                    : ShopFreightTemplateCreate;
                const res = await api(data);
                if (res.code === 0) {
                    this.$layer.msg(this.isEdit ? "编辑成功" : "添加成功");
                    this.$emit("success");
                    this.visibleLocal = false;
                } else {
                    this.$layer.msg(res.msg || "保存失败");
                }
            } catch (e) {
                this.$layer.msg("保存失败");
            } finally {
                this.submitting = false;
            }
        },
        onCancel() {
            this.visibleLocal = false;
        }
    }
};
</script>

<style scoped lang="scss">
.edit-body {
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 8px;

    .section-title {
        font-size: 15px;
        font-weight: 600;
        color: #1a1a1a;
        text-align: left;
        margin: 20px 0 12px;

        &:first-child {
            margin-top: 0;
        }

        .section-sub {
            font-size: 13px;
            color: #666666;
            font-weight: 400;
            margin-left: 4px;

            &.optional {
                color: #2d8cf0;
            }
        }

        .section-tip {
            font-size: 12px;
            color: #999999;
            font-weight: 400;
            margin-left: 8px;
        }
    }

    .base-form {
        .from-address-box {
            display: flex;
            column-gap: 12px;

            .region-select {
                flex: 1;
            }
        }

        .switch-box {
            display: flex;
            align-items: center;
            column-gap: 10px;

            .switch-label {
                font-size: 14px;
                color: #1a1a1a;
            }

            .switch-tip {
                font-size: 12px;
                color: #999999;
            }
        }
    }

    .default-rule,
    .inline-rule {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        column-gap: 8px;
        row-gap: 8px;
        font-size: 14px;
        color: #666666;

        .rule-label {
            color: #999999;
        }

        .rule-unit {
            white-space: nowrap;
        }
    }

    .default-rule {
        background: #f8f9fb;
        border-radius: 6px;
        padding: 16px;

        &.disabled {
            opacity: 0.5;
            pointer-events: none;
        }
    }

    .region-box {
        border: 1px solid #e8e8e8;
        border-radius: 6px;
        padding: 16px;

        .region-table {
            width: 100%;
            border-collapse: collapse;

            th,
            td {
                padding: 10px 8px;
                text-align: left;
                font-size: 14px;
                color: #666666;
                border-bottom: 1px solid #f0f0f0;
            }

            th {
                color: #333333;
                font-weight: 500;
                background: #fafafa;
            }

            .th-province {
                width: 35%;
            }
            .th-rule {
                width: 55%;
            }
            .th-action {
                width: 10%;
            }

            .delete-link {
                color: #ff5967;
                cursor: pointer;

                &:hover {
                    color: #ff7a85;
                }
            }
        }

        .add-region-btn {
            margin-top: 12px;
            width: 100%;
        }
    }
}

.default-checkbox {
    margin-top: 12px;
    text-align: left;
}

.edit-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    column-gap: 16px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;

    .save-btn {
        background-color: #ff5967;
        border-color: #ff5967;
        color: #ffffff;

        &:hover {
            background-color: #ff7a85;
            border-color: #ff7a85;
        }
    }
}
</style>
